import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { C, SourceTag, ErrorBox, fmtDateTime, fmtINR, leadName, errOf, inputStyle, btn } from './LeadForms';

/**
 * ABM / HO review queue — /leads/review (managers and super admin). Server: /api/leads/review.
 *  - Order at another store (or online) whose phone matches an open lead: approve → link the order
 *    (Customer, credited to the store that owns the lead); dismiss → leave the lead as it is.
 *  - A store asked to move a lead: approve → the lead moves (owner cleared, open tasks move with it).
 */
const card = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12 };
const pill = (on) => ({ padding: '6px 14px', borderRadius: 18, border: `1px solid ${on ? C.brand : C.border}`, background: on ? C.brand : '#fff', color: on ? '#fff' : C.muted, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' });

export default function LeadReview({ storeId, stores, onStoreChange }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [targets, setTargets] = useState({});
  const load = useCallback(() => {
    setRows(null); setError('');
    api.get('/leads/review', { params: { storeId: storeId || 'all', status } }).then(r => setRows(r.data.reviews || [])).catch(e => setError(errOf(e, 'Could not load the review queue')));
  }, [storeId, status]);
  useEffect(() => { load(); }, [load]);
  const act = async (r, action) => {
    setBusy(r.id); setError('');
    try {
      const res = await api.post(`/leads/review/${r.id}/${action}`, { note: notes[r.id] || '', storeId: targets[r.id] || undefined });
      setToast(res.data.message || (action === 'dismiss' ? 'Dismissed' : 'Approved')); setTimeout(() => setToast(''), 3500);
      window.dispatchEvent(new Event('leads-changed'));
      load();
    } catch (e) { setError(errOf(e, 'Could not save')); }
    setBusy(null);
  };
  const storeChoices = (stores || []).filter(s => s.id !== 'CX');

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/leads')} style={btn('secondary')}>‹ Leads</button>
        <h2 style={{ margin: 0, fontSize: 20, color: C.text, fontWeight: 800 }}>Review queue</h2>
        {stores && (
          <select value={storeId} onChange={e => onStoreChange(e.target.value)} style={{ ...inputStyle, width: 'auto', maxWidth: 240, fontWeight: 600 }}>
            <option value="all">All Stores</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ flex: 1 }} />
        {[['pending', 'Waiting'], ['approved', 'Approved'], ['dismissed', 'Dismissed']].map(([k, l]) => <button key={k} onClick={() => setStatus(k)} style={pill(status === k)}>{l}</button>)}
      </div>
      {toast && <div style={{ marginBottom: 12, padding: '9px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>{toast}</div>}
      <ErrorBox text={error} />
      {!rows ? (!error && <div style={{ padding: 40, textAlign: 'center', color: C.faint }}>Loading…</div>) : rows.length === 0 ? (
        <div style={{ ...card, padding: 30, textAlign: 'center', color: C.faint, fontSize: 13 }}>{status === 'pending' ? 'Nothing waiting for review.' : 'Nothing here.'}</div>
      ) : rows.map(r => {
        const order = r.review_type === 'order_match';
        return (
          <div key={r.id} style={{ ...card, padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: order ? '#ecfdf5' : '#fff7ed', color: order ? '#047857' : '#c2410c' }}>{order ? '🛒 Order match' : '↪️ Move request'}</span>
              <b style={{ fontSize: 14, color: C.text, cursor: 'pointer' }} onClick={() => navigate(`/leads/${r.lead_id}`)}>{leadName(r)} ›</b>
              <span style={{ fontSize: 12, color: C.muted }}>{r.lead_code} · +91 {r.phone} · {r.lead_store_name}</span>
              <SourceTag source={r.source} />
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: C.faint }}>{fmtDateTime(r.created_at)}{r.requested_by && r.requested_by !== 'system' ? ` · by ${r.requested_by}` : ''}</span>
            </div>
            <div style={{ fontSize: 13, color: C.text, marginTop: 8 }}>
              {order
                ? <>Order <b>{r.order_name}</b> · {fmtINR(r.order_value)} placed {r.order_store_id ? <>at <b>{r.order_store_name || r.order_store_id}</b></> : <b>online</b>} on {fmtDateTime(r.order_created_at)}. The lead belongs to <b>{r.lead_store_name}</b>.</>
                : <>Move to <b>{r.target_store_name || 'a store the reviewer picks'}</b> — “{r.reason}”</>}
            </div>
            {r.status !== 'pending' ? (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{r.status === 'approved' ? 'Approved' : 'Dismissed'} by {r.resolved_by} · {fmtDateTime(r.resolved_at)}{r.resolution_note ? ` — ${r.resolution_note}` : ''}</div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {!order && !r.target_store_id && (
                  <select value={targets[r.id] || ''} onChange={e => setTargets(t => ({ ...t, [r.id]: e.target.value }))} style={{ ...inputStyle, width: 220 }}>
                    <option value="">Move to…</option>
                    {storeChoices.filter(s => s.id !== String(r.store_id)).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                <input value={notes[r.id] || ''} onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))} placeholder="Note (optional)" style={{ ...inputStyle, flex: 1, minWidth: 180 }} />
                <button disabled={busy === r.id || (!order && !r.target_store_id && !targets[r.id])} onClick={() => act(r, 'approve')} style={btn('primary', busy === r.id || (!order && !r.target_store_id && !targets[r.id]))}>
                  {order ? 'Link order to this lead' : 'Move lead'}
                </button>
                <button disabled={busy === r.id} onClick={() => act(r, 'dismiss')} style={btn('secondary', busy === r.id)}>Dismiss</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
